import { useState, useEffect, useCallback, useRef } from 'react';
import { ChatMessage, AIAssistantState } from '../models/general';
import { sendChatMessage, getAIStatus, AIChatInput, getConversations, getConversation, deleteConversation } from '../api/AI/chat';

const INITIAL_STATE: AIAssistantState = {
  isOpen: false,
  isMinimized: false,
  isLoading: false,
  isConfigured: false,
  availableToolsCount: 0,
  statusMessage: '',
  currentSession: {
    conversationId: null,
    messages: [],
  },
  error: null,
  // CO-225 — null until AI/Status answers, so the widget renders no indicator rather than a
  // placeholder that would flash a wrong number on every mount.
  quota: null,
};

export function useAIChat() {
  const [state, setState] = useState<AIAssistantState>(INITIAL_STATE);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Check AI status on mount
  useEffect(() => {
    checkAIStatus();
  }, []);

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    scrollToBottom();
  }, [state.currentSession.messages]);

  // Load most recent conversation when widget opens
  useEffect(() => {
    if (state.isOpen && state.isConfigured && state.currentSession.messages.length === 0) {
      loadMostRecentConversation();
    }
  }, [state.isOpen, state.isConfigured]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const loadMostRecentConversation = async () => {
    try {
      const result = await getConversations();
      if (result.status === 200 && result.payload && result.payload.length > 0) {
        // Load the most recent conversation
        const mostRecent = result.payload[0];
        const detailResult = await getConversation(mostRecent.id);
        if (detailResult.status === 200 && detailResult.payload) {
          const messages: ChatMessage[] = detailResult.payload.messages.map(m => ({
            id: `${m.role}-${m.id}`,
            role: m.role,
            content: m.content,
            timestamp: new Date(m.timestamp),
          }));
          setState(prev => ({
            ...prev,
            currentSession: {
              conversationId: mostRecent.id,
              messages,
            },
          }));
        }
      }
    } catch (error) {
      console.error('Failed to load conversation:', error);
    }
  };

  const checkAIStatus = async () => {
    try {
      const result = await getAIStatus();
      if (result.status === 200 && result.payload) {
        setState((prev) => ({
          ...prev,
          isConfigured: result.payload!.isConfigured,
          availableToolsCount: result.payload!.availableToolsCount,
          statusMessage: result.payload!.message,
          quota: {
            exempt: result.payload!.quotaExempt,
            used: result.payload!.quotaUsed,
            limit: result.payload!.quotaLimit,
            resetsAtUtc: result.payload!.quotaResetsAtUtc,
          },
        }));
      }
    } catch (error) {
      console.error('Failed to check AI status:', error);
    }
  };

  const toggleOpen = useCallback(() => {
    setState((prev) => ({ ...prev, isOpen: !prev.isOpen }));
  }, []);

  const close = useCallback(() => {
    setState((prev) => ({ ...prev, isOpen: false }));
  }, []);

  const addMessage = useCallback((message: ChatMessage) => {
    setState((prev) => ({
      ...prev,
      currentSession: {
        ...prev.currentSession,
        messages: [...prev.currentSession.messages, message],
      },
    }));
  }, []);

  const sendMessage = async (content: string) => {
    if (!content.trim() || state.isLoading) return;

    // Add user message
    const userMessage: ChatMessage = {
      id: `user-${Date.now()}`,
      role: 'user',
      content: content.trim(),
      timestamp: new Date(),
    };
    addMessage(userMessage);

    // Set loading state
    setState((prev) => ({ ...prev, isLoading: true, error: null }));

    try {
      const input: AIChatInput = {
        message: content.trim(),
        conversationId: state.currentSession.conversationId,
      };

      const result = await sendChatMessage(input);

      if (result.status === 200 && result.payload) {
        // Add assistant response
        const assistantMessage: ChatMessage = {
          id: `assistant-${Date.now()}`,
          role: 'assistant',
          content: result.payload.message,
          timestamp: new Date(),
          navigation: result.payload.navigation,
        };

        setState((prev) => ({
          ...prev,
          isLoading: false,
          currentSession: {
            conversationId: result.payload!.conversationId,
            messages: [...prev.currentSession.messages, assistantMessage],
          },
          // CO-225 — the server charged one unit for this request; reflect it immediately so the
          // count the user sees is the count they have, not the count they had on page load.
          quota: prev.quota && !prev.quota.exempt
            ? { ...prev.quota, used: Math.min(prev.quota.used + 1, prev.quota.limit) }
            : prev.quota,
        }));
      } else {
        // Handle error from API
        const errorMessage: ChatMessage = {
          id: `error-${Date.now()}`,
          role: 'assistant',
          content: result.errors?.[0]?.description || 'Failed to get response from AI',
          timestamp: new Date(),
          isError: true,
        };
        addMessage(errorMessage);
        setState((prev) => ({ ...prev, isLoading: false }));
      }
    } catch (error: any) {
      console.error('Failed to send message:', error);
      // CO-225 — a 429 means the daily quota (or a burst limit) refused the turn. The axios
      // interceptor already explained it in a toast, so don't stack a second generic message in the
      // transcript; just re-sync the authoritative counters so the indicator shows 0 left.
      const httpStatus = error?.__httpStatus ?? error?.response?.status;
      if (httpStatus === 429) {
        setState((prev) => ({ ...prev, isLoading: false }));
        checkAIStatus();
        return;
      }
      const errorMessage: ChatMessage = {
        id: `error-${Date.now()}`,
        role: 'assistant',
        content: 'Sorry, I encountered an error. Please try again.',
        timestamp: new Date(),
        isError: true,
      };
      addMessage(errorMessage);
      setState((prev) => ({
        ...prev,
        isLoading: false,
        error: error.message || 'Unknown error occurred',
      }));
    }
  };

  const clearChat = useCallback(async () => {
    const conversationId = state.currentSession.conversationId;

    // Clear local state immediately for responsive UI
    setState((prev) => ({
      ...prev,
      currentSession: {
        conversationId: null,
        messages: [],
      },
      error: null,
    }));

    // Delete conversation from backend if it exists
    if (conversationId) {
      try {
        await deleteConversation(conversationId);
      } catch (error) {
        console.error('Failed to delete conversation:', error);
      }
    }
  }, [state.currentSession.conversationId]);

  return {
    ...state,
    toggleOpen,
    close,
    sendMessage,
    clearChat,
    messagesEndRef,
  };
}
